// ─────────────────────────────────────────────────────────────────────────────
//  countries.ts  —  Frontend mirror of backend/src/lib/countries.ts.
//
//  Why a parallel copy: backend and frontend live in separate npm packages
//  with no shared-types workspace yet (queued under /docs/TODO.md "Backend
//  type hardening + shared-types package"). Until that lands, this file is
//  the FE-side source of truth. Drift surface = ~50 ISO-2 codes; if the
//  backend list changes, mirror it here.
//
//  Display-side concerns live here too: flag emoji (regional indicator
//  pairs are mechanical from ISO-2) and the i18n key for the country name.
//  Localized names live in frontend/src/i18n/{en,lt}.ts under t.country.names.
// ─────────────────────────────────────────────────────────────────────────────

export const EUROPEAN_COUNTRY_CODES = [
  'AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ',
  'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GR', 'HR', 'HU',
  'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME',
  'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE',
  'SI', 'SK', 'SM', 'TR', 'UA', 'VA', 'XK',
] as const;

export type CountryCode = (typeof EUROPEAN_COUNTRY_CODES)[number];

/** Convert an ISO 3166-1 alpha-2 code to its flag emoji (regional indicators). */
export function flagEmoji(code: string): string {
  if (typeof code !== 'string' || code.length !== 2) return '';
  const A = 0x1F1E6 - 'A'.charCodeAt(0);
  const cc = code.toUpperCase();
  return String.fromCodePoint(A + cc.charCodeAt(0)) + String.fromCodePoint(A + cc.charCodeAt(1));
}

let flagEmojiSupport: boolean | undefined;

/**
 * #527 — does this browser draw a flag emoji? Chrome and Edge on Windows have
 * no flag glyphs: they draw the two regional indicator letters ("LT") one by
 * one. A browser with flags joins the two letters into ONE glyph, so the flag
 * is narrower than its two letters drawn one at a time; without flags, the
 * two widths are equal. The check measures once, with the font of the page.
 * With no canvas (a privacy extension can block it) the answer is "no": the
 * fallback (the code in a pill) is readable everywhere.
 */
export function supportsFlagEmoji(): boolean {
  if (flagEmojiSupport !== undefined) return flagEmojiSupport;
  flagEmojiSupport = false;
  const canvas = document.createElement('canvas');
  try {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `32px ${getComputedStyle(document.body).fontFamily || 'sans-serif'}`;
      const [l, t] = [...flagEmoji('LT')];
      const pair = ctx.measureText(l + t).width;
      const apart = ctx.measureText(l).width + ctx.measureText(t).width;
      flagEmojiSupport = pair > 0 && pair < apart * 0.8;
    }
  } catch { /* no canvas: keep the pill */ }
  return flagEmojiSupport;
}

export function isValidCountry(code: unknown): code is CountryCode {
  return typeof code === 'string' && (EUROPEAN_COUNTRY_CODES as readonly string[]).includes(code);
}

/**
 * #661 — the options of a country filter: the countries that the rows hold,
 * sorted by code, each with its name from `nameOf`. A `selected` country that
 * no row holds (a link with `?country=ZZ`, #627) stays an option, last: with
 * no option for it, a select shows its first option ("All countries") while
 * the list is filtered. The file has no import, so the caller gives `nameOf`.
 */
export function countryFilterOptions(
  codes: ReadonlyArray<string | null | undefined>,
  selected: string,
  nameOf: (code: string) => string,
): { value: string; label: string }[] {
  const present = [...new Set(codes.filter((c): c is string => !!c))].sort();
  if (selected && !present.includes(selected)) present.push(selected);
  return present.map(code => ({ value: code, label: nameOf(code) }));
}
